// src/fileUploader.js

import React, { useState, useEffect } from 'react';
import { authFetch } from './utils/authFetch';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import IconWithProgress from './IconWithProgress';

// Import your tab/subtab constants
import { 
  MAIN_TABS, 
  GOVERNANCE_SUBTABS, 
  AI_CONTROL_SUBTABS,
  CATEGORIES
} from './utils/policyConstants';
import { uploadViaSupabase } from './utils/supabaseUpload';

// If you have a separate constants file for categories, import it; otherwise define it here:

// Styled Components for aesthetics
const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: '#f9f9f9',
  minHeight: '100vh',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
}));


const FileUpload = () => {
  // Move DocumentUploadSection inside the component to access retryJob
  const DocumentUploadSection = ({
    title,
    allowedTypes,
    onFileChange,
    onUpload,
    uploadedFiles,
    handleEmbed,
    handleDelete,
    embeddingFiles,
    embeddingJobs,
  }) => (
    <StyledPaper sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom color="textPrimary">
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <input
          type="file"
          accept={allowedTypes}
          multiple
          onChange={onFileChange}
          style={{ display: 'none' }}
          id={`${title}-input`}
        />
        <label htmlFor={`${title}-input`}>
          <StyledButton
            variant="outlined"
            color="primary"
            component="span"
            startIcon={<AddIcon />}
          >
            Add
          </StyledButton>
        </label>
      </Box>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Allowed file types: {allowedTypes.replace(/,/g, ', ')}
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Date Added</TableCell>
              <TableCell align="center">Upload</TableCell>
              <TableCell align="center">Embed</TableCell>
              <TableCell align="center">Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
              uploadedFiles.map((file, idx) => (
                <TableRow key={idx}>
                  <TableCell>{file.name}</TableCell>
                  <TableCell>{file.date}</TableCell>

                  <TableCell align="center">
                    <Tooltip title="Upload File">
                      <IconButton
                        color="primary"
                        onClick={() => onUpload(file)}
                        disabled={file.uploaded || file.isUploading}
                      >
                        {file.isUploading ? <CircularProgress size={24} /> : <CloudUploadOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    {embeddingJobs[file.name] ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {['timeout', 'failed'].includes(embeddingJobs[file.name].status) ? (
                          <Tooltip title="Retry embedding">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => retryJob(embeddingJobs[file.name].jobId, file.name)}
                            >
                              <CloudUploadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={`${embeddingJobs[file.name].status}: ${embeddingJobs[file.name].progress}%`}>
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={embeddingJobs[file.name].progress}
                                size={24}
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" component="div" color="text.secondary">
                                  {embeddingJobs[file.name].progress}%
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {embeddingJobs[file.name].status}
                        </Typography>
                      </Box>
                    ) : (
                      <IconWithProgress
                        onClick={() => handleEmbed(file.name, file.fileType, file.category)}
                        isLoading={embeddingFiles[file.name] || false}
                        tooltip="Embed File"
                        disabled={!file.canEmbed}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Delete File">
                      <IconButton
                        color="secondary"
                        onClick={() => handleDelete(file.name, file.category)}
                      >
                        <DeleteOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No files added yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </StyledPaper>
  );
  const [mainTabIndex, setMainTabIndex] = useState(MAIN_TABS.RISK_CATALOGUES);
  const [govSubTabIndex, setGovSubTabIndex] = useState(GOVERNANCE_SUBTABS.AI_GOVERNANCE_POLICIES);
  const [modelCtrlSubTabIndex, setModelCtrlSubTabIndex] = useState(AI_CONTROL_SUBTABS.DATA_GOVERNANCE_POLICIES);

  // We store file arrays under each category key
  const [uploadedFiles, setUploadedFiles] = useState({
    [CATEGORIES.RISK_CATALOGUES]: [],
    [CATEGORIES.RISK_POLICIES]: [],
    [CATEGORIES.AUDIT_POLICIES]: [],
    [CATEGORIES.AI_GOVERNANCE_POLICIES]: [],
    [CATEGORIES.SECURITY_POLICIES]: [],
    [CATEGORIES.BIAS_POLICIES]: [],
    [CATEGORIES.DATA_GOVERNANCE_POLICIES]: [],
    [CATEGORIES.LOGGING_MONITORING_POLICIES]: [],
    [CATEGORIES.QUALITY_TEST_POLICIES]: [],
    [CATEGORIES.INCIDENT_MANAGEMENT_POLICIES]: [],
    [CATEGORIES.DOCUMENTATION_RECORD_KEEPING_POLICIES]: []
  });

  // For tracking embedding state by filename
  const [embeddingFiles, setEmbeddingFiles] = useState({});

  // For tracking async embedding jobs
  const [embeddingJobs, setEmbeddingJobs] = useState({}); // { fileName: { jobId, status, progress } }

  // Storage option selection: 'netlify' (current) or 'supabase' (new)
  const [storageOption, setStorageOption] = useState('netlify');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null); // { name, category }

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Fetch existing uploaded files on mount (for each category)
  useEffect(() => {
    const fetchUploadedFiles = async () => {
      try {
        const categories = Object.keys(uploadedFiles);
        const allUploadedFiles = {};

        // Create an array of promises for each category fetch
        const fetchPromises = categories.map((category) =>
          authFetch('/.netlify/functions/listCatalogues', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ category }),
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Failed to fetch files for category: ${category}`);
              }
              return response.json();
            })
            .then((filesData) => ({
              category,
              filesData: filesData.map((f) => ({
                ...f,
                isUploading: false,
                // canEmbed is true if 'uploaded' is true
                canEmbed: !!f.uploaded,
                originalFile: null
              })),
            }))
        );

        // Wait for all fetches
        const results = await Promise.all(fetchPromises);

        // Aggregate results
        results.forEach(({ category, filesData }) => {
          allUploadedFiles[category] = filesData;
        });

        setUploadedFiles(allUploadedFiles);
        console.log('Uploaded files fetched successfully:', allUploadedFiles);
      } catch (error) {
        console.error('Error fetching uploaded files:', error);
        setSnackbarMessage(`Error fetching files: ${error.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MAIN TAB (4 possible values)
  const handleMainTabChange = (event, newValue) => {
    setMainTabIndex(newValue);
  };

  // GOVERNANCE SUB-TABS
  const handleGovSubTabChange = (event, newValue) => {
    setGovSubTabIndex(newValue);
  };

  // MODEL CONTROL SUB-TABS
  const handleModelCtrlSubTabChange = (event, newValue) => {
    setModelCtrlSubTabIndex(newValue);
  };

  // Based on the current mainTab + subTab, return the correct category string
  const getCurrentCategory = () => {
    // 0) Risk Catalogues & Registers
    if (mainTabIndex === MAIN_TABS.RISK_CATALOGUES) {
      return CATEGORIES.RISK_CATALOGUES; 
    }

    // 1) Governance Policies
    if (mainTabIndex === MAIN_TABS.GOVERNANCE_POLICIES) {
      switch (govSubTabIndex) {
        case GOVERNANCE_SUBTABS.RISK_POLICIES:
          return CATEGORIES.RISK_POLICIES;
        case GOVERNANCE_SUBTABS.AUDIT_POLICIES:
          return CATEGORIES.AUDIT_POLICIES;
        case GOVERNANCE_SUBTABS.AI_GOVERNANCE_POLICIES:
          return CATEGORIES.AI_GOVERNANCE_POLICIES;
        default:
          return CATEGORIES.RISK_POLICIES;
      }
    }

    // 2) Model Control Baseline
    if (mainTabIndex === MAIN_TABS.AI_CONTROL_BASELINE) {
      switch (modelCtrlSubTabIndex) {
        case AI_CONTROL_SUBTABS.BIAS_POLICIES:
          return CATEGORIES.BIAS_POLICIES;
        case AI_CONTROL_SUBTABS.DATA_GOVERNANCE_POLICIES:
          return CATEGORIES.DATA_GOVERNANCE_POLICIES;
        case AI_CONTROL_SUBTABS.LOGGING_MONITORING_POLICIES:
          return CATEGORIES.LOGGING_MONITORING_POLICIES;
        case AI_CONTROL_SUBTABS.QUALITY_TEST_POLICIES:
          return CATEGORIES.QUALITY_TEST_POLICIES;
        case AI_CONTROL_SUBTABS.INCIDENT_MANAGEMENT_POLICIES:
          return CATEGORIES.INCIDENT_MANAGEMENT_POLICIES;
        case AI_CONTROL_SUBTABS.DOCUMENTATION_RECORD_KEEPING_POLICIES:
          return CATEGORIES.DOCUMENTATION_RECORD_KEEPING_POLICIES;
        default:
          return CATEGORIES.BIAS_POLICIES;
      }
    }

    // 3) Security Policies
    if (mainTabIndex === MAIN_TABS.SECURITY_POLICIES) {
      return CATEGORIES.SECURITY_POLICIES;
    }

    // Fallback
    return '';
  };

  // Restrict allowed file types per category
  const getAllowedTypes = (category) => {
    // You can customize per category or keep them all the same
    const types = {
      [CATEGORIES.RISK_CATALOGUES]: '.pdf,.txt,.csv',
      [CATEGORIES.RISK_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.AUDIT_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.AI_GOVERNANCE_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.SECURITY_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.BIAS_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.DATA_GOVERNANCE_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.LOGGING_MONITORING_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.QUALITY_TEST_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.INCIDENT_MANAGEMENT_POLICIES]: '.pdf,.txt,.csv',
      [CATEGORIES.DOCUMENTATION_RECORD_KEEPING_POLICIES]: '.pdf,.txt,.csv',
    };
    return types[category] || '.pdf,.txt,.csv';
  };

  // User selected files from the input
  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const currentCategory = getCurrentCategory();
    const allowed = getAllowedTypes(currentCategory);

    if (!currentCategory) {
      console.error('No valid category selected.');
      setSnackbarMessage('No valid category selected.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!allowed) {
      console.error(`No allowed types defined for category: ${currentCategory}`);
      setSnackbarMessage(`No allowed types defined for category: ${currentCategory}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Filter out invalid file types
    const validFiles = selectedFiles.filter((file) => {
      const fileType = file.name.split('.').pop().toLowerCase();
      return allowed.includes(`.${fileType}`);
    });

    if (validFiles.length === 0) {
      setSnackbarMessage('No valid files selected.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    // Map them into our structure
    const newFiles = validFiles.map((file) => ({
      name: file.name,
      date: new Date().toLocaleString(),
      uploaded: false, // will be true after we actually upload it
      fileType: file.name.split('.').pop().toLowerCase(),
      category: currentCategory,
      isUploading: false,
      canEmbed: false,
      originalFile: file
    }));

    setUploadedFiles((prev) => ({
      ...prev,
      [currentCategory]: [...(prev[currentCategory] || []), ...newFiles],
    }));
  };

  // Upload file (model-cards can use Supabase; others remain as-is for now)
  const handleUpload = async (file) => {
    const { category, name, originalFile } = file;

    // Mark isUploading = true
    setUploadedFiles((prev) => ({
      ...prev,
      [category]: prev[category].map((f) =>
        f.name === name ? { ...f, isUploading: true } : f
      ),
    }));

    try {
      if (storageOption === 'supabase') {
        // New Supabase upload path
        const bucket = 'risk-catalogues';
        const path = `${category}/${name}`;
        
        await uploadViaSupabase({ 
          file: originalFile, 
          bucket, 
          path, 
          contentType: originalFile.type 
        });
      } else {
        // Keep existing Netlify Blobs flow
        const formData = new FormData();
        formData.append('file', originalFile);
        formData.append('category', category);

        const response = await authFetch('/.netlify/functions/blobManager', { method: 'POST', body: formData });
        if (!response.ok) {
          let msg = 'Failed to upload file';
          try { const errorData = await response.json(); msg = errorData.error || msg; } catch {}
          throw new Error(msg);
        }
      }

      // Mark file as "uploaded" and canEmbed = true
      setUploadedFiles((prev) => ({
        ...prev,
        [category]: prev[category].map((f) =>
          f.name === name
            ? { ...f, uploaded: true, isUploading: false, canEmbed: true }
            : f
        ),
      }));

      const storageType = storageOption === 'supabase' ? 'Supabase' : 'Netlify Blobs';
      setSnackbarMessage(`File "${name}" uploaded to ${storageType} successfully!`);
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadedFiles((prev) => ({
        ...prev,
        [category]: prev[category].map((f) =>
          f.name === name ? { ...f, isUploading: false } : f
        ),
      }));
      setSnackbarMessage(`Error uploading "${name}": ${error.message}`);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  // Delete file from blob (Netlify function: deleteCatalogue)
  const handleDelete = (fileName, category) => {
    // Set the file to delete and open the confirmation modal
    setFileToDelete({ name: fileName, category });
    setConfirmDeleteOpen(true);
  };

  // Confirm deletion after user confirms in the modal
  const confirmDelete = async () => {
    if (!fileToDelete) {
      setConfirmDeleteOpen(false);
      return;
    }

    const { name, category } = fileToDelete;

    try {
      const response = await authFetch('/.netlify/functions/deleteCatalogue', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: name, category }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file and embeddings');
      }

      // Remove from local state
      setUploadedFiles((prev) => ({
        ...prev,
        [category]: prev[category].filter((file) => file.name !== name),
      }));

      setSnackbarMessage(`File "${name}" and its embeddings deleted successfully!`);
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Error deleting file and embeddings:', error);
      setSnackbarMessage(`Error deleting "${name}": ${error.message}`);
      setSnackbarSeverity('error');
    } finally {
      setConfirmDeleteOpen(false);
      setFileToDelete(null);
      setSnackbarOpen(true);
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setConfirmDeleteOpen(false);
    setFileToDelete(null);
  };

  // Retry a stuck or failed job
  const retryJob = async (jobId, fileName) => {
    try {
      console.log(`Retrying job ${jobId} for ${fileName}`);
      const response = await authFetch('/.netlify/functions/retryEmbeddingJob', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to retry job');
      }
      
      // Start polling again
      pollJobStatus(jobId, fileName);
      
      setSnackbarMessage(`Retrying embedding for "${fileName}"...`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error retrying job:', error);
      setSnackbarMessage(`Failed to retry "${fileName}": ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Poll job status for async embedding
  const pollJobStatus = async (jobId, fileName) => {
    const maxAttempts = 60; // 5 minutes max (5s intervals)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await authFetch(`/.netlify/functions/getEmbeddingJobStatus?jobId=${jobId}`);
        if (!response.ok) {
          throw new Error('Failed to get job status');
        }
        
        const data = await response.json();
        const job = data.job;
        
        // Update job status in state
        setEmbeddingJobs(prev => ({
          ...prev,
          [fileName]: {
            jobId: job.jobId,
            status: job.status,
            progress: job.progress,
            chunksProcessed: job.chunksProcessed,
            totalChunks: job.totalChunks,
            errorMessage: job.errorMessage
          }
        }));
        
        if (job.status === 'completed') {
          // Job completed successfully
          setEmbeddingFiles(prev => ({ ...prev, [fileName]: false }));
          setEmbeddingJobs(prev => {
            const newJobs = { ...prev };
            delete newJobs[fileName];
            return newJobs;
          });
          setSnackbarMessage(`File "${fileName}" processed and embedded successfully!`);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          return;
        } else if (job.status === 'failed') {
          // Job failed
          setEmbeddingFiles(prev => ({ ...prev, [fileName]: false }));
          setEmbeddingJobs(prev => {
            const newJobs = { ...prev };
            delete newJobs[fileName];
            return newJobs;
          });
          setSnackbarMessage(`Error embedding "${fileName}": ${job.errorMessage || 'Unknown error'}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        } else if (attempts < maxAttempts) {
          // Continue polling
          attempts++;
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          // Timeout - but keep the job in state so user can retry
          setEmbeddingFiles(prev => ({ ...prev, [fileName]: false }));
          setEmbeddingJobs(prev => ({
            ...prev,
            [fileName]: {
              ...prev[fileName],
              status: 'timeout',
              errorMessage: 'Polling timeout - job may still be processing'
            }
          }));
          setSnackbarMessage(`Timeout waiting for "${fileName}" to complete embedding - you can retry if needed`);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setEmbeddingFiles(prev => ({ ...prev, [fileName]: false }));
          setEmbeddingJobs(prev => {
            const newJobs = { ...prev };
            delete newJobs[fileName];
            return newJobs;
          });
          setSnackbarMessage(`Error checking status for "${fileName}": ${error.message}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    };
    
    poll();
  };

  // Embed file into chosen storage (Pinecone or Supabase)
  const handleEmbed = async (fileName, fileType, category) => {
    // Show loading indicator
    setEmbeddingFiles((prev) => ({
      ...prev,
      [fileName]: true,
    }));

    try {
      if (storageOption === 'supabase') {
        // Use async endpoint for Supabase to avoid timeouts
        const response = await authFetch('/.netlify/functions/embedCatalogueSupabaseAsync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileName, fileType, category }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to start embedding job');
        }

        const result = await response.json();
        
        // Start polling for job status
        setEmbeddingJobs(prev => ({
          ...prev,
          [fileName]: {
            jobId: result.jobId,
            status: 'queued',
            progress: 0
          }
        }));
        
        const estimatedTime = fileType === 'pdf' ? '2-5 minutes' : '30-60 seconds';
        setSnackbarMessage(`Embedding job started for "${fileName}". Estimated time: ${estimatedTime}`);
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        
        // Start polling
        pollJobStatus(result.jobId, fileName);
        
      } else {
        // Use original sync endpoint for Netlify/Pinecone
        const response = await authFetch('/.netlify/functions/processFile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileName, fileType, category }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to process and embed file');
        }

        setSnackbarMessage(`File "${fileName}" processed and embedded to Pinecone successfully!`);
        setSnackbarSeverity('success');
        setEmbeddingFiles((prev) => ({ ...prev, [fileName]: false }));
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error processing and embedding file:', error);
      setSnackbarMessage(`Error embedding "${fileName}": ${error.message}`);
      setSnackbarSeverity('error');
      setEmbeddingFiles((prev) => ({ ...prev, [fileName]: false }));
      setSnackbarOpen(true);
    }
  };

  // Snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <StyledBox>
      <Typography variant="h4" gutterBottom color="textPrimary">
        Catalogues, Policies & Procedures
      </Typography>

      {/* Storage Option Toggle */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          Storage & Embedding Destination
        </Typography>
        <Tabs
          value={storageOption}
          onChange={(event, newValue) => setStorageOption(newValue)}
          textColor="secondary"
          indicatorColor="secondary"
          variant="standard"
        >
          <Tab 
            label="Pinecone + Netlify (Current)" 
            value="netlify"
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Supabase (New - Secure)" 
            value="supabase"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          {storageOption === 'supabase' 
            ? 'Files will be stored in Supabase with vector embeddings for enhanced security'
            : 'Files will be stored in Netlify Blobs with Pinecone vector embeddings (current system)'
          }
        </Typography>
      </Box>

      {/* Main Tabs: Risk Catalogues, Governance, Model Control, Security */}
      <Tabs
        value={mainTabIndex}
        onChange={handleMainTabChange}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="Risk Catalogues & Registers" />
        <Tab label="Corporate Governance" />
        <Tab label="AI Control Baseline" />
        <Tab label="Security" />
      </Tabs>

      {/* ===== RISK CATALOGUES TAB ===== */}
      {mainTabIndex === MAIN_TABS.RISK_CATALOGUES && (
        <DocumentUploadSection
          title="Risk Catalogues"
          allowedTypes={getAllowedTypes(CATEGORIES.RISK_CATALOGUES)}
          onFileChange={handleFileChange}
          onUpload={handleUpload}
          handleEmbed={handleEmbed}
          handleDelete={handleDelete}
          uploadedFiles={uploadedFiles[CATEGORIES.RISK_CATALOGUES] || []}
          embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
        />
      )}

      {/* ===== GOVERNANCE POLICIES TAB ===== */}
      {mainTabIndex === MAIN_TABS.GOVERNANCE_POLICIES && (
        <Box sx={{ mt: 2 }}>
          <Tabs value={govSubTabIndex} onChange={handleGovSubTabChange}>
            <Tab label="Risk Policies" />
            <Tab label="Audit Policies" />
            <Tab label="AI Governance Policies" />
          </Tabs>

          {govSubTabIndex === GOVERNANCE_SUBTABS.RISK_POLICIES && (
            <DocumentUploadSection
              title="Risk Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.RISK_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.RISK_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {govSubTabIndex === GOVERNANCE_SUBTABS.AUDIT_POLICIES && (
            <DocumentUploadSection
              title="Audit Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.AUDIT_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.AUDIT_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {govSubTabIndex === GOVERNANCE_SUBTABS.AI_GOVERNANCE_POLICIES && (
            <DocumentUploadSection
              title="AI Governance Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.AI_GOVERNANCE_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.AI_GOVERNANCE_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
        </Box>
      )}

      {/* ===== AI CONTROL BASELINE TAB ===== */}
      {mainTabIndex === MAIN_TABS.AI_CONTROL_BASELINE && (
        <Box sx={{ mt: 2 }}>
          <Tabs
            value={modelCtrlSubTabIndex}
            onChange={handleModelCtrlSubTabChange}
            variant="scrollable"
          >
           <Tab label="Data Governance & Management" />
            <Tab label="Bias & Fairness" />
            <Tab label="Logging/Monitoring" />
            <Tab label="Quality/Test" />
            <Tab label="Incident Management" />
            <Tab label="Documentation/Record Keeping" />
          </Tabs>

          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.DATA_GOVERNANCE_POLICIES && (
            <DocumentUploadSection
              title="Data Governance & Management Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.DATA_GOVERNANCE_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.DATA_GOVERNANCE_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.BIAS_POLICIES && (
            <DocumentUploadSection
              title="Bias Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.BIAS_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.BIAS_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          
          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.LOGGING_MONITORING_POLICIES && (
            <DocumentUploadSection
              title="Logging & Monitoring Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.LOGGING_MONITORING_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.LOGGING_MONITORING_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.QUALITY_TEST_POLICIES && (
            <DocumentUploadSection
              title="Quality/Test Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.QUALITY_TEST_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.QUALITY_TEST_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.INCIDENT_MANAGEMENT_POLICIES && (
            <DocumentUploadSection
              title="Incident Management Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.INCIDENT_MANAGEMENT_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.INCIDENT_MANAGEMENT_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
          {modelCtrlSubTabIndex === AI_CONTROL_SUBTABS.DOCUMENTATION_RECORD_KEEPING_POLICIES && (
            <DocumentUploadSection
              title="Documentation & Record-Keeping Policies"
              allowedTypes={getAllowedTypes(CATEGORIES.DOCUMENTATION_RECORD_KEEPING_POLICIES)}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
              handleEmbed={handleEmbed}
              handleDelete={handleDelete}
              uploadedFiles={uploadedFiles[CATEGORIES.DOCUMENTATION_RECORD_KEEPING_POLICIES] || []}
              embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
            />
          )}
        </Box>
      )}

      {/* ===== SECURITY POLICIES TAB ===== */}
      {mainTabIndex === MAIN_TABS.SECURITY_POLICIES && (
        <DocumentUploadSection
          title="Security Policies"
          allowedTypes={getAllowedTypes(CATEGORIES.SECURITY_POLICIES)}
          onFileChange={handleFileChange}
          onUpload={handleUpload}
          handleEmbed={handleEmbed}
          handleDelete={handleDelete}
          uploadedFiles={uploadedFiles[CATEGORIES.SECURITY_POLICIES] || []}
          embeddingFiles={embeddingFiles}
          embeddingJobs={embeddingJobs}
        />
      )}

      <Dialog
        open={confirmDeleteOpen}
        onClose={cancelDelete}
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
      >
        <DialogTitle id="confirm-delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-dialog-description">
            Deleting the catalogue will remove its embeddings from the search index. This action cannot be undone.
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* Global Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

export default FileUpload;
